import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiFetch } from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import GanttChart, { GanttLegend } from '../../components/shared/GanttChart';
import { printGantt, printProjectOverview, printProjectTasks } from '../../utils/printGantt';

const STATUS_OPTIONS = ['planning', 'active', 'on_hold', 'completed', 'cancelled'];
const STATUS_STYLE = {
  planning:  { bg: '#ede9fe', color: '#7c3aed' },
  active:    { bg: '#d1fae5', color: '#065f46' },
  on_hold:   { bg: '#fef3c7', color: '#b45309' },
  completed: { bg: '#dcfce7', color: '#166534' },
  cancelled: { bg: '#f1f5f9', color: '#64748b' },
};

const MILESTONE_STATUS = {
  pending:     { bg: '#f1f5f9', color: '#64748b' },
  in_progress: { bg: '#fef3c7', color: '#b45309' },
  completed:   { bg: '#d1fae5', color: '#065f46' },
};

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

function durationDays(start, end) {
  if (!start || !end) return null;
  return Math.round((new Date(end + 'T00:00:00Z') - new Date(start + 'T00:00:00Z')) / 86400000) + 1;
}

function Badge({ label, style }) {
  return <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 9px', borderRadius: 99, textTransform: 'capitalize', ...style }}>{label}</span>;
}

const TABS = ['Overview', 'Tasks', 'Gantt', 'Messages'];

export default function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [proj, setProj] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('Overview');
  const [msg, setMsg] = useState(null);
  const [agents, setAgents] = useState([]);

  // Task/milestone state
  const [expandedMs, setExpandedMs] = useState({});
  const [addTaskMs, setAddTaskMs] = useState(null);
  const [newTaskTitle, setNewTaskTitle] = useState('');
  const [newTaskDates, setNewTaskDates] = useState({ planned_start: '', planned_end: '' });
  const [newTaskResp, setNewTaskResp] = useState('KGreen');
  const [addingMilestone, setAddingMilestone] = useState(false);
  const [newMsTitle, setNewMsTitle] = useState('');
  const [editTaskId, setEditTaskId] = useState(null);
  const [taskForm, setTaskForm] = useState({});
  const [savingTask, setSavingTask] = useState(false);

  // Message state
  const [msgContent, setMsgContent] = useState('');
  const [msgInternal, setMsgInternal] = useState(true);
  const [sendingMsg, setSendingMsg] = useState(false);

  // Inline edit
  const [editStatus, setEditStatus] = useState(false);
  const [showConvert, setShowConvert] = useState(false);

  // Apply template to existing project
  const [showApplyTpl, setShowApplyTpl] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [applyTplId, setApplyTplId] = useState('');
  const [applyingTpl, setApplyingTpl] = useState(false);
  async function openApplyTpl() {
    setApplyTplId(''); setShowApplyTpl(true);
    try { setTemplates(await apiFetch('/project-templates')); } catch (e) { notify('error', e.message); }
  }
  async function applyTemplate() {
    if (!applyTplId) return;
    setApplyingTpl(true);
    try {
      const r = await apiFetch(`/projects/${id}/apply-template`, { method: 'POST', body: { template_id: applyTplId } });
      setShowApplyTpl(false);
      await load();
      const parts = [];
      if (r.milestonesAdded) parts.push(`${r.milestonesAdded} milestone${r.milestonesAdded === 1 ? '' : 's'} added`);
      if (r.milestonesMatched) parts.push(`${r.milestonesMatched} merged into existing`);
      if (r.tasksAdded) parts.push(`${r.tasksAdded} task${r.tasksAdded === 1 ? '' : 's'} added`);
      if (r.tasksSkipped) parts.push(`${r.tasksSkipped} duplicate${r.tasksSkipped === 1 ? '' : 's'} skipped`);
      notify('success', `Applied “${r.template}” — ${parts.join(', ') || 'nothing new to add'}.`);
    } catch (e) { notify('error', e.message); } finally { setApplyingTpl(false); }
  }
  const [convertForm, setConvertForm] = useState({ amc_start_date: '', amc_end_date: '', serial_number: '' });

  function notify(type, text) { setMsg({ type, text }); setTimeout(() => setMsg(null), 5000); }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiFetch(`/projects/${id}`);
      setProj(data);
    } catch (e) { notify('error', e.message); }
    setLoading(false);
    // Agents list is optional — fetch separately so a failure never blocks the project
    try {
      const usr = await apiFetch('/users/agents');
      setAgents((Array.isArray(usr) ? usr : (usr.users || [])).filter(u => ['admin','manager','agent'].includes(u.role)));
    } catch (_) {}
  }, [id]);

  useEffect(() => { load(); }, [load]);

  // ── Status update ──────────────────────────────────────────────────────────
  async function updateStatus(status) {
    try {
      await apiFetch(`/projects/${id}`, { method: 'PATCH', body: { status } });
      setProj(p => ({ ...p, status }));
      setEditStatus(false);
    } catch (e) { notify('error', e.message); }
  }

  // ── Delete project (admin only) ──────────────────────────────────────────────
  async function handleDelete() {
    if (!window.confirm(`Delete project "${proj.title}" (${proj.project_number})?\n\nThis permanently removes all its milestones, tasks and messages. This cannot be undone.`)) return;
    try {
      await apiFetch(`/projects/${id}`, { method: 'DELETE' });
      navigate('/admin/projects');
    } catch (e) { notify('error', e.message); }
  }

  // ── Task toggle ────────────────────────────────────────────────────────────
  async function toggleTask(tid, done) {
    try {
      await apiFetch(`/projects/${id}/tasks/${tid}`, { method: 'PATCH', body: { done: done ? 1 : 0 } });
      // Reload so milestone status AND actual dates (auto-filled on completion) refresh
      // everywhere — including the Gantt tab and milestone headers.
      await load();
    } catch (e) { notify('error', e.message); }
  }

  // ── Add task ───────────────────────────────────────────────────────────────
  async function addTask(milestoneId) {
    if (!newTaskTitle.trim()) return;
    const body = { title: newTaskTitle.trim(), responsibility: newTaskResp };
    if (newTaskDates.planned_start) body.planned_start = newTaskDates.planned_start;
    if (newTaskDates.planned_end)   body.planned_end = newTaskDates.planned_end;
    try {
      await apiFetch(`/projects/${id}/milestones/${milestoneId}/tasks`, { method: 'POST', body });
      setNewTaskTitle('');
      setNewTaskDates({ planned_start: '', planned_end: '' });
      setNewTaskResp('KGreen');
      setAddTaskMs(null);
      await load(); // reload — task dates can reshape milestone span & cascade later ones
    } catch (e) { notify('error', e.message); }
  }

  // ── Edit task (dates / % complete) ───────────────────────────────────────────
  function openTaskEdit(t) {
    setEditTaskId(t.id);
    setTaskForm({
      title: t.title || '',
      planned_start: t.planned_start || '',
      planned_end: t.planned_end || '',
      actual_start: t.actual_start || '',
      actual_end: t.actual_end || '',
      pct_complete: t.pct_complete ?? 0,
      responsibility: t.responsibility || 'KGreen',
    });
  }

  async function saveTaskEdit(tid) {
    setSavingTask(true);
    try {
      await apiFetch(`/projects/${id}/tasks/${tid}`, {
        method: 'PATCH',
        body: {
          title: taskForm.title,
          planned_start: taskForm.planned_start || '',
          planned_end: taskForm.planned_end || '',
          actual_start: taskForm.actual_start || '',
          actual_end: taskForm.actual_end || '',
          pct_complete: taskForm.pct_complete === '' ? 0 : Number(taskForm.pct_complete),
          responsibility: taskForm.responsibility || 'KGreen',
        },
      });
      setEditTaskId(null);
      await load(); // reload — planned-date edits cascade the whole schedule
    } catch (e) { notify('error', e.message); }
    setSavingTask(false);
  }

  async function deleteTask(tid) {
    try {
      await apiFetch(`/projects/${id}/tasks/${tid}`, { method: 'DELETE' });
      setEditTaskId(null);
      await load();
    } catch (e) { notify('error', e.message); }
  }

  // ── Save current plan as a personal template ─────────────────────────────────
  async function saveAsTemplate() {
    const name = window.prompt('Name this personal template (only you will see it):', `${proj.title} template`);
    if (!name || !name.trim()) return;
    try {
      await apiFetch(`/projects/${id}/save-as-template`, { method: 'POST', body: { name: name.trim() } });
      notify('success', `Saved "${name.trim()}" to your personal templates`);
    } catch (e) { notify('error', e.message); }
  }

  // ── Add milestone ──────────────────────────────────────────────────────────
  async function addMilestone() {
    if (!newMsTitle.trim()) return;
    try {
      await apiFetch(`/projects/${id}/milestones`, { method: 'POST', body: { title: newMsTitle.trim() } });
      setNewMsTitle('');
      setAddingMilestone(false);
      await load(); // reload — recompute shifts other milestones' dates (Gantt must reflect all)
    } catch (e) { notify('error', e.message); }
  }

  // ── Milestone status toggle ────────────────────────────────────────────────
  async function toggleMilestone(ms) {
    const next = ms.status === 'completed' ? 'in_progress' : 'completed';
    try {
      await apiFetch(`/projects/${id}/milestones/${ms.id}`, { method: 'PATCH', body: { status: next } });
      await load();
    } catch (e) { notify('error', e.message); }
  }

  // ── Send message ───────────────────────────────────────────────────────────
  async function sendMessage(e) {
    e.preventDefault();
    if (!msgContent.trim()) return;
    setSendingMsg(true);
    try {
      const newMsg = await apiFetch(`/projects/${id}/messages`, {
        method: 'POST', body: { content: msgContent.trim(), is_internal: msgInternal },
      });
      setProj(p => ({ ...p, messages: [...(p.messages || []), newMsg] }));
      setMsgContent('');
    } catch (e) { notify('error', e.message); }
    setSendingMsg(false);
  }

  // ── Team management (admin/manager) ──────────────────────────────────────────
  async function addTeamMember(userId) {
    if (!userId) return;
    try {
      await apiFetch(`/projects/${id}/team`, { method: 'POST', body: { user_id: userId, role: 'member' } });
      load();
    } catch (e) { notify('error', e.message); }
  }

  async function removeTeamMember(userId) {
    try {
      await apiFetch(`/projects/${id}/team/${userId}`, { method: 'DELETE' });
      load();
    } catch (e) { notify('error', e.message); }
  }

  // ── Convert prospect ───────────────────────────────────────────────────────
  async function convertProspect(e) {
    e.preventDefault();
    try {
      await apiFetch(`/projects/${id}/convert`, { method: 'POST', body: convertForm });
      notify('success', 'Customer converted to active');
      setShowConvert(false);
      load();
    } catch (e) { notify('error', e.message); }
  }

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)' }}>Loading…</div>;
  if (!proj) return <div style={{ padding: 40, textAlign: 'center', color: '#dc2626' }}>Project not found</div>;

  const canManageTeam = ['admin', 'super_admin', 'manager'].includes(user.role);
  const ss = STATUS_STYLE[proj.status] || STATUS_STYLE.planning;
  const totalTasks = proj.milestones.reduce((a, m) => a + m.tasks.length, 0);
  const doneTasks  = proj.milestones.reduce((a, m) => a + m.tasks.filter(t => t.done).length, 0);
  const progPct    = totalTasks > 0 ? Math.round((doneTasks / totalTasks) * 100) : 0;

  return (
    <div className="page">
      {/* Breadcrumb */}
      <div style={{ marginBottom: 16, fontSize: 13, color: 'var(--text-secondary)' }}>
        <button onClick={() => navigate('/admin/projects')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#0B6623', fontWeight: 600, padding: 0 }}>
          ← Projects
        </button>
        <span style={{ margin: '0 8px' }}>·</span>
        <span style={{ fontFamily: 'monospace' }}>{proj.project_number}</span>
      </div>

      {msg && (
        <div style={{ marginBottom: 16, padding: '10px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600,
          background: msg.type === 'success' ? '#d1fae5' : '#fee2e2',
          border: `1px solid ${msg.type === 'success' ? '#6ee7b7' : '#fca5a5'}`,
          color: msg.type === 'success' ? '#065f46' : '#dc2626' }}>
          {msg.text}
        </div>
      )}

      {/* Header card */}
      <div style={{ background: 'var(--bg-surface)', borderRadius: 12, border: '1px solid var(--border-subtle)', padding: '20px 24px', marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 6 }}>{proj.title}</h1>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ fontSize: 12, fontFamily: 'monospace', color: '#94a3b8' }}>{proj.project_number}</span>
              <span style={{ fontSize: 12, background: proj.type === 'POC' ? '#fef9c3' : '#dbeafe', color: proj.type === 'POC' ? '#854d0e' : '#1e40af', padding: '2px 8px', borderRadius: 99, fontWeight: 700 }}>{proj.type}</span>
              {editStatus ? (
                <select autoFocus onBlur={() => setEditStatus(false)}
                  value={proj.status} onChange={e => updateStatus(e.target.value)}
                  style={{ fontSize: 12, padding: '2px 8px', borderRadius: 99, border: '1.5px solid #0B6623', fontWeight: 700, background: ss.bg, color: ss.color, cursor: 'pointer' }}>
                  {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
                </select>
              ) : (
                <button onClick={() => setEditStatus(true)}
                  style={{ ...ss, fontSize: 12, padding: '2px 10px', borderRadius: 99, fontWeight: 700, border: 'none', cursor: 'pointer', textTransform: 'capitalize' }}>
                  {proj.status.replace('_', ' ')} ▾
                </button>
              )}
              {proj.customer_stage === 'prospect' && (
                <span style={{ fontSize: 11, background: '#fef3c7', color: '#b45309', padding: '2px 8px', borderRadius: 99, fontWeight: 700 }}>PROSPECT</span>
              )}
            </div>
          </div>

          <div style={{ display: 'flex', gap: 10 }}>
            {proj.customer_stage === 'prospect' && ['admin','manager'].includes(user.role) && (
              <button className="btn btn-primary btn-sm" onClick={() => setShowConvert(true)}>
                Convert to Customer
              </button>
            )}
            {['admin', 'super_admin'].includes(user.role) && (
              <button className="btn btn-danger btn-sm" onClick={handleDelete}>
                Delete Project
              </button>
            )}
          </div>
        </div>

        {/* Meta row */}
        <div style={{ display: 'flex', gap: 24, marginTop: 16, flexWrap: 'wrap', fontSize: 13 }}>
          {[
            ['Customer', proj.customer_name],
            ['Product', proj.product_name || '—'],
            ['Manager', proj.manager_name || '—'],
            ['Start', fmtDate(proj.start_date)],
            ['Target', fmtDate(proj.target_date)],
          ].map(([lbl, val]) => (
            <div key={lbl}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: .5 }}>{lbl}</div>
              <div style={{ fontWeight: 500, color: 'var(--text-primary)', marginTop: 2 }}>{val}</div>
            </div>
          ))}
        </div>

        {/* Progress bar */}
        {totalTasks > 0 && (
          <div style={{ marginTop: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--text-secondary)', marginBottom: 4 }}>
              <span>Progress</span>
              <span>{doneTasks}/{totalTasks} tasks · {progPct}%</span>
            </div>
            <div style={{ height: 8, background: 'var(--n-100)', borderRadius: 99, overflow: 'hidden' }}>
              <div style={{ width: `${progPct}%`, height: '100%', background: '#0B6623', borderRadius: 99, transition: 'width .4s' }} />
            </div>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 0, borderBottom: '2px solid var(--border-subtle)', marginBottom: 20 }}>
        {TABS.map(t => (
          <button key={t} onClick={() => setTab(t)}
            style={{ padding: '10px 24px', background: 'none', border: 'none', cursor: 'pointer',
              fontSize: 14, fontWeight: 700,
              color: tab === t ? '#0B6623' : 'var(--text-secondary)',
              borderBottom: `3px solid ${tab === t ? '#0B6623' : 'transparent'}`,
              marginBottom: -2, transition: 'all .15s' }}>
            {t}
          </button>
        ))}
      </div>

      {/* Tab: Overview */}
      {tab === 'Overview' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 20, alignItems: 'flex-start' }}>
          <div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
              <button className="btn btn-secondary btn-sm" onClick={() => printProjectOverview(proj)} style={{ whiteSpace: 'nowrap' }}>🖨 Print (A4)</button>
            </div>
            {proj.notes && (
              <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', borderRadius: 10, padding: '14px 16px', marginBottom: 16 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: .5, marginBottom: 6 }}>Notes</div>
                <div style={{ fontSize: 13, color: 'var(--text-primary)', lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>{proj.notes}</div>
              </div>
            )}
            {/* Milestone overview */}
            <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', borderRadius: 12, padding: '16px 20px' }}>
              <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 14 }}>Milestones</div>
              {proj.milestones.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 24, color: 'var(--text-secondary)', fontSize: 13 }}>No milestones. Go to Tasks tab to add them.</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {proj.milestones.map(m => {
                    const ms = MILESTONE_STATUS[m.status] || MILESTONE_STATUS.pending;
                    const total = m.tasks.length;
                    const done = m.tasks.filter(t => t.done).length;
                    const pct = total > 0 ? (done / total) * 100 : 0;
                    return (
                      <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <Badge label={m.status.replace('_', ' ')} style={ms} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>{m.title}</div>
                          <div style={{ height: 4, background: 'var(--n-100)', borderRadius: 99, overflow: 'hidden' }}>
                            <div style={{ width: `${pct}%`, height: '100%', background: '#0B6623', borderRadius: 99 }} />
                          </div>
                        </div>
                        <span style={{ fontSize: 12, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>{done}/{total}</span>
                        {/* Planned (blue) + Actual (green if ≤ plan, red if late) */}
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1, whiteSpace: 'nowrap' }}>
                          {m.start_date && m.end_date ? (
                            <span style={{ fontSize: 11, fontWeight: 600, color: '#2563eb' }}>
                              {fmtDate(m.start_date)} – {fmtDate(m.end_date)} · {durationDays(m.start_date, m.end_date)}d
                            </span>
                          ) : m.due_date && <span style={{ fontSize: 11, color: '#2563eb' }}>{fmtDate(m.due_date)}</span>}
                          {(m.actual_start || m.actual_end) && (
                            <span style={{ fontSize: 10.5, fontWeight: 600 }}>
                              <span style={{ color: '#94a3b8' }}>act </span>
                              {m.actual_start && (
                                <span style={{ color: (!m.start_date || m.actual_start <= m.start_date) ? '#16a34a' : '#dc2626' }}>{fmtDate(m.actual_start)}</span>
                              )}
                              {m.actual_end && (
                                <>
                                  <span style={{ color: '#94a3b8' }}> – </span>
                                  <span style={{ color: (!m.end_date || m.actual_end <= m.end_date) ? '#16a34a' : '#dc2626' }}>{fmtDate(m.actual_end)}</span>
                                </>
                              )}
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Team card */}
          <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', borderRadius: 12, padding: '16px 20px' }}>
            <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 14 }}>Team</div>
            {(proj.team || []).length === 0 ? (
              <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>No team members</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {proj.team.map(m => (
                  <div key={m.user_id} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'linear-gradient(135deg,#0B6623,#19a843)',
                                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  fontSize: 12, fontWeight: 800, color: '#fff', flexShrink: 0 }}>
                      {m.name?.[0]?.toUpperCase()}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600 }}>{m.name}</div>
                      <div style={{ fontSize: 11, color: '#94a3b8', textTransform: 'capitalize' }}>{m.role}</div>
                    </div>
                    {canManageTeam && m.role !== 'lead' && (
                      <button onClick={() => removeTeamMember(m.user_id)} title="Remove from project"
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', fontSize: 16, lineHeight: 1, padding: '2px 6px' }}>×</button>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Add member (admin/manager) */}
            {canManageTeam && (() => {
              const teamIds = new Set((proj.team || []).map(m => m.user_id));
              const available = agents.filter(a => !teamIds.has(a.id));
              return (
                <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--border-subtle)' }}>
                  <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', display: 'block', marginBottom: 5 }}>
                    ADD TEAM MEMBER
                  </label>
                  <select className="input" value=""
                    onChange={e => addTeamMember(e.target.value)}
                    disabled={available.length === 0}>
                    <option value="">{available.length === 0 ? 'Everyone is already on the team' : 'Select a person…'}</option>
                    {available.map(a => (
                      <option key={a.id} value={a.id}>{a.name} · {a.role}</option>
                    ))}
                  </select>
                  <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 6 }}>
                    Members can open and work on this project from their own login.
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {/* Tab: Tasks */}
      {tab === 'Tasks' && (
        <div>
          {/* Toolbar */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginBottom: 12 }}>
            <button onClick={() => printProjectTasks(proj)}
              title="Print the task schedule (planned & actual dates) on A4"
              style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-secondary)', background: 'var(--bg-surface)', border: '1.5px solid var(--border-subtle)', borderRadius: 8, padding: '7px 14px', cursor: 'pointer' }}>
              🖨 Print (A4)
            </button>
            <button onClick={openApplyTpl}
              title="Add a template's milestones & tasks to this project (duplicates are skipped)"
              style={{ fontSize: 12.5, fontWeight: 700, color: '#0B6623', background: '#f0fdf4',
                       border: '1.5px solid #bbf7d0', borderRadius: 8, padding: '7px 14px', cursor: 'pointer' }}>
              ＋ Apply Template
            </button>
            <button onClick={saveAsTemplate} disabled={!proj.milestones.length}
              title="Save these milestones & tasks as your own reusable template"
              style={{ fontSize: 12.5, fontWeight: 700, color: '#0B6623', background: '#f0fdf4',
                       border: '1.5px solid #bbf7d0', borderRadius: 8, padding: '7px 14px',
                       cursor: proj.milestones.length ? 'pointer' : 'not-allowed', opacity: proj.milestones.length ? 1 : 0.5 }}>
              ⭑ Save as My Template
            </button>
          </div>
          {proj.milestones.map((m, mIdx) => {
            const ms = MILESTONE_STATUS[m.status] || MILESTONE_STATUS.pending;
            const isOpen = expandedMs[m.id] !== false;
            return (
              <div key={m.id} style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', borderRadius: 12, marginBottom: 12, overflow: 'hidden' }}>
                {/* Milestone header */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px',
                              background: 'var(--bg-app)', borderBottom: isOpen ? '1px solid var(--border-subtle)' : 'none',
                              cursor: 'pointer' }}
                     onClick={() => setExpandedMs(e => ({ ...e, [m.id]: !isOpen }))}>
                  <button onClick={e => { e.stopPropagation(); toggleMilestone(m); }}
                    style={{ width: 20, height: 20, borderRadius: '50%', border: `2px solid ${m.status === 'completed' ? '#0B6623' : '#cbd5e1'}`,
                             background: m.status === 'completed' ? '#0B6623' : 'transparent', cursor: 'pointer',
                             display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    {m.status === 'completed' && <span style={{ color: '#fff', fontSize: 10, fontWeight: 900 }}>✓</span>}
                  </button>
                  <span style={{ fontWeight: 700, fontSize: 14, flex: 1, color: 'var(--text-primary)' }}>
                    M{mIdx + 1}: {m.title}
                  </span>
                  <Badge label={m.status.replace('_', ' ')} style={ms} />
                  {/* Planned (blue) + Actual (green if on/before plan, red if late) */}
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2, whiteSpace: 'nowrap' }}>
                    {m.start_date && m.end_date ? (
                      <span style={{ fontSize: 12, fontWeight: 600, color: '#2563eb' }}>
                        Plan: {fmtDate(m.start_date)} – {fmtDate(m.end_date)}
                        <span style={{ marginLeft: 6, fontWeight: 700 }}>{durationDays(m.start_date, m.end_date)}d</span>
                      </span>
                    ) : m.due_date && <span style={{ fontSize: 12, color: '#2563eb' }}>Plan: {fmtDate(m.due_date)}</span>}
                    {(m.actual_start || m.actual_end) && (
                      <span style={{ fontSize: 11.5, fontWeight: 600 }}>
                        <span style={{ color: 'var(--text-secondary)' }}>Actual: </span>
                        {m.actual_start && (
                          <span style={{ color: (!m.start_date || m.actual_start <= m.start_date) ? '#16a34a' : '#dc2626' }}>
                            {fmtDate(m.actual_start)}
                          </span>
                        )}
                        {m.actual_end && (
                          <>
                            <span style={{ color: 'var(--text-secondary)' }}> – </span>
                            <span style={{ color: (!m.end_date || m.actual_end <= m.end_date) ? '#16a34a' : '#dc2626' }}>
                              {fmtDate(m.actual_end)}
                            </span>
                          </>
                        )}
                      </span>
                    )}
                  </div>
                  <span style={{ fontSize: 13, color: 'var(--text-secondary)', marginLeft: 4 }}>{isOpen ? '▲' : '▼'}</span>
                </div>

                {/* Tasks */}
                {isOpen && (
                  <div style={{ padding: '10px 16px 14px' }}>
                    {m.tasks.map(t => {
                      const editing = editTaskId === t.id;
                      const lbl = { fontSize: 10.5, fontWeight: 700, color: 'var(--text-secondary)', display: 'block', marginBottom: 3, textTransform: 'uppercase', letterSpacing: .3 };
                      const di = { width: '100%', padding: '6px 8px', borderRadius: 6, border: '1.5px solid var(--n-300)', fontSize: 12.5, background: 'var(--bg-surface)', color: 'var(--text-primary)', outline: 'none', boxSizing: 'border-box' };
                      return (
                        <div key={t.id} style={{ borderBottom: '1px solid var(--border-subtle)', padding: '8px 0' }}>
                          {/* Row summary */}
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <input type="checkbox" checked={!!t.done}
                              onChange={e => toggleTask(t.id, e.target.checked)}
                              style={{ width: 16, height: 16, cursor: 'pointer', accentColor: '#0B6623', flexShrink: 0 }} />
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: 13, color: 'var(--text-primary)',
                                            textDecoration: t.done ? 'line-through' : 'none', opacity: t.done ? 0.6 : 1 }}>{t.title}</div>
                              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginTop: 3, fontSize: 11, color: '#94a3b8' }}>
                                {(() => {
                                  const rsp = t.responsibility || 'KGreen';
                                  const st = rsp === 'Client' ? { bg: '#fef3c7', c: '#92400e' }
                                           : rsp === 'KG + Client' ? { bg: '#dbeafe', c: '#1e40af' }
                                           : { bg: '#dcfce7', c: '#166534' };
                                  return <span style={{ fontWeight: 700, padding: '1px 7px', borderRadius: 99, background: st.bg, color: st.c }}>{rsp}</span>;
                                })()}
                                {(t.planned_start || t.planned_end) && <span>📅 Plan: {fmtDate(t.planned_start)} – {fmtDate(t.planned_end)}</span>}
                                {(t.actual_start || t.actual_end) && <span>✅ Actual: {fmtDate(t.actual_start)} – {fmtDate(t.actual_end)}</span>}
                                {t.assignee_name && <span>👤 {t.assignee_name}</span>}
                              </div>
                            </div>
                            {/* % complete pill */}
                            <span style={{ fontSize: 11, fontWeight: 700, flexShrink: 0,
                                           color: (t.pct_complete >= 100) ? '#065f46' : '#b45309',
                                           background: (t.pct_complete >= 100) ? '#d1fae5' : '#fef3c7',
                                           padding: '2px 8px', borderRadius: 99 }}>{t.pct_complete || 0}%</span>
                            <button onClick={() => editing ? setEditTaskId(null) : openTaskEdit(t)}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#0B6623', fontSize: 12, fontWeight: 700, flexShrink: 0 }}>
                              {editing ? 'Close' : '✎ Edit'}
                            </button>
                          </div>

                          {/* Inline editor */}
                          {editing && (
                            <div style={{ marginTop: 10, padding: 12, background: 'var(--bg-app)', borderRadius: 8 }}>
                              <div style={{ marginBottom: 10 }}>
                                <label style={lbl}>Task Title</label>
                                <input style={di} value={taskForm.title} onChange={e => setTaskForm(f => ({ ...f, title: e.target.value }))} />
                              </div>
                              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
                                <div>
                                  <label style={lbl}>Planned Start</label>
                                  <input type="date" style={di} value={taskForm.planned_start} onChange={e => setTaskForm(f => ({ ...f, planned_start: e.target.value }))} />
                                </div>
                                <div>
                                  <label style={lbl}>Planned Completion</label>
                                  <input type="date" style={di} value={taskForm.planned_end} onChange={e => setTaskForm(f => ({ ...f, planned_end: e.target.value }))} />
                                </div>
                                <div>
                                  <label style={lbl}>Actual Start</label>
                                  <input type="date" style={di} value={taskForm.actual_start} onChange={e => setTaskForm(f => ({ ...f, actual_start: e.target.value }))} />
                                </div>
                                <div>
                                  <label style={lbl}>Actual Completion</label>
                                  <input type="date" style={di} value={taskForm.actual_end} onChange={e => setTaskForm(f => ({ ...f, actual_end: e.target.value }))} />
                                </div>
                                <div style={{ gridColumn: '1/-1' }}>
                                  <label style={lbl}>Responsibility</label>
                                  <select style={di} value={taskForm.responsibility} onChange={e => setTaskForm(f => ({ ...f, responsibility: e.target.value }))}>
                                    <option value="KGreen">KGreen</option>
                                    <option value="Client">Client</option>
                                    <option value="KG + Client">KG + Client</option>
                                  </select>
                                </div>
                                <div style={{ gridColumn: '1/-1' }}>
                                  <label style={lbl}>% Completion — {taskForm.pct_complete || 0}%</label>
                                  <input type="range" min={0} max={100} step={5} style={{ width: '100%', accentColor: '#0B6623' }}
                                    value={taskForm.pct_complete || 0} onChange={e => setTaskForm(f => ({ ...f, pct_complete: Number(e.target.value) }))} />
                                </div>
                              </div>
                              <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 10 }}>
                                Planned dates set this milestone's duration and shift the milestones that follow.
                              </div>
                              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                                <button className="btn btn-danger btn-sm" onClick={() => deleteTask(t.id)}>Delete</button>
                                <button className="btn btn-ghost btn-sm" onClick={() => setEditTaskId(null)}>Cancel</button>
                                <button className="btn btn-primary btn-sm" onClick={() => saveTaskEdit(t.id)} disabled={savingTask}>
                                  {savingTask ? 'Saving…' : 'Save'}
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}

                    {/* Add task inline */}
                    {addTaskMs === m.id ? (
                      <div style={{ marginTop: 10, padding: 12, background: 'var(--bg-app)', borderRadius: 8 }}>
                        <input autoFocus className="input" style={{ fontSize: 13, marginBottom: 8 }} placeholder="Task title…"
                          value={newTaskTitle} onChange={e => setNewTaskTitle(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter') addTask(m.id); if (e.key === 'Escape') { setAddTaskMs(null); setNewTaskTitle(''); } }} />
                        <div style={{ marginBottom: 10 }}>
                          <label style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--text-secondary)', display: 'block', marginBottom: 3, textTransform: 'uppercase' }}>Responsibility</label>
                          <select className="input" style={{ fontSize: 12.5 }} value={newTaskResp} onChange={e => setNewTaskResp(e.target.value)}>
                            <option value="KGreen">KGreen</option>
                            <option value="Client">Client</option>
                            <option value="KG + Client">KG + Client</option>
                          </select>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
                          <div>
                            <label style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--text-secondary)', display: 'block', marginBottom: 3, textTransform: 'uppercase' }}>Planned Start</label>
                            <input type="date" className="input" style={{ fontSize: 12.5 }} value={newTaskDates.planned_start} onChange={e => setNewTaskDates(d => ({ ...d, planned_start: e.target.value }))} />
                          </div>
                          <div>
                            <label style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--text-secondary)', display: 'block', marginBottom: 3, textTransform: 'uppercase' }}>Planned Completion</label>
                            <input type="date" className="input" style={{ fontSize: 12.5 }} value={newTaskDates.planned_end} onChange={e => setNewTaskDates(d => ({ ...d, planned_end: e.target.value }))} />
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                          <button className="btn btn-ghost btn-sm" onClick={() => { setAddTaskMs(null); setNewTaskTitle(''); setNewTaskDates({ planned_start: '', planned_end: '' }); }}>Cancel</button>
                          <button className="btn btn-primary btn-sm" onClick={() => addTask(m.id)}>Add Task</button>
                        </div>
                      </div>
                    ) : (
                      <button onClick={() => { setAddTaskMs(m.id); setNewTaskTitle(''); setNewTaskDates({ planned_start: m.start_date || proj.start_date || '', planned_end: m.end_date || '' }); }}
                        style={{ marginTop: 8, fontSize: 12, color: '#0B6623', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600, padding: '4px 0' }}>
                        + Add task
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          {/* Add milestone */}
          {addingMilestone ? (
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <input autoFocus className="input" placeholder="Milestone title…"
                value={newMsTitle} onChange={e => setNewMsTitle(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') addMilestone(); if (e.key === 'Escape') { setAddingMilestone(false); setNewMsTitle(''); } }} />
              <button className="btn btn-primary btn-sm" onClick={addMilestone}>Add</button>
              <button className="btn btn-ghost btn-sm" onClick={() => { setAddingMilestone(false); setNewMsTitle(''); }}>×</button>
            </div>
          ) : (
            <button onClick={() => setAddingMilestone(true)}
              style={{ marginTop: 4, width: '100%', padding: '12px 0', borderRadius: 10, border: '1.5px dashed var(--border-subtle)',
                       background: 'transparent', color: 'var(--text-secondary)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
              + Add Milestone
            </button>
          )}
        </div>
      )}

      {/* Tab: Gantt */}
      {tab === 'Gantt' && (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
            <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Planned vs actual timeline for milestones &amp; tasks.</div>
            <div style={{ marginLeft: 'auto' }}><GanttLegend /></div>
            <button className="btn btn-secondary btn-sm" onClick={() => printGantt(proj)} style={{ whiteSpace: 'nowrap' }}>🖨 Print (A4)</button>
          </div>
          <div className="card" style={{ padding: 16 }}>
            <GanttChart project={proj} />
          </div>
        </div>
      )}

      {/* Tab: Messages */}
      {tab === 'Messages' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
          {/* Thread */}
          <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', borderRadius: 12, padding: '16px 20px', marginBottom: 16, maxHeight: 480, overflowY: 'auto' }}>
            {(proj.messages || []).length === 0 ? (
              <div style={{ textAlign: 'center', padding: 24, color: 'var(--text-secondary)', fontSize: 13 }}>No messages yet</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {proj.messages.map(m => {
                  const isInbound = m.direction === 'inbound';
                  const isInternal = m.is_internal;
                  return (
                    <div key={m.id} style={{ display: 'flex', flexDirection: isInbound ? 'row' : 'row-reverse', gap: 10 }}>
                      <div style={{ width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
                                    background: isInbound ? '#dbeafe' : isInternal ? '#f1f5f9' : '#d1fae5',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    fontSize: 12, fontWeight: 800, color: isInbound ? '#1e40af' : isInternal ? '#64748b' : '#065f46' }}>
                        {(m.sender_display || m.sender_name || '?')[0]?.toUpperCase()}
                      </div>
                      <div style={{ maxWidth: '75%' }}>
                        <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 3, textAlign: isInbound ? 'left' : 'right' }}>
                          {m.sender_display || m.sender_name || m.from_email}
                          {isInternal && <span style={{ marginLeft: 6, background: '#f1f5f9', color: '#64748b', padding: '1px 6px', borderRadius: 99, fontSize: 10, fontWeight: 700 }}>Internal</span>}
                          {isInbound && <span style={{ marginLeft: 6, background: '#dbeafe', color: '#1e40af', padding: '1px 6px', borderRadius: 99, fontSize: 10, fontWeight: 700 }}>Email Reply</span>}
                        </div>
                        <div style={{ background: isInbound ? '#f0f9ff' : isInternal ? 'var(--n-50)' : '#f0fdf4',
                                      border: `1px solid ${isInbound ? '#bae6fd' : isInternal ? 'var(--border-subtle)' : '#bbf7d0'}`,
                                      borderRadius: 10, padding: '10px 14px', fontSize: 13, lineHeight: 1.6,
                                      color: 'var(--text-primary)', whiteSpace: 'pre-wrap' }}>
                          {m.content}
                        </div>
                        <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 3, textAlign: isInbound ? 'left' : 'right' }}>
                          {new Date(m.created_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Compose */}
          <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', borderRadius: 12, padding: '16px 20px' }}>
            <div style={{ display: 'flex', gap: 12, marginBottom: 10 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 13 }}>
                <input type="radio" checked={msgInternal} onChange={() => setMsgInternal(true)} style={{ accentColor: '#0B6623' }} />
                Internal note
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 13 }}>
                <input type="radio" checked={!msgInternal} onChange={() => setMsgInternal(false)} style={{ accentColor: '#0B6623' }} />
                Email to customer <span style={{ fontSize: 11, color: '#94a3b8' }}>({proj.customer_email || 'no email set'})</span>
              </label>
            </div>
            <form onSubmit={sendMessage}>
              <textarea className="input" rows={3} style={{ resize: 'vertical', marginBottom: 10, fontSize: 13 }}
                placeholder={msgInternal ? 'Add an internal note…' : `Send email to ${proj.customer_email || 'customer'}…`}
                value={msgContent} onChange={e => setMsgContent(e.target.value)} />
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button type="submit" className="btn btn-primary btn-sm" disabled={sendingMsg || !msgContent.trim()}>
                  {sendingMsg ? 'Sending…' : msgInternal ? 'Add Note' : 'Send Email'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Convert prospect modal */}
      {showConvert && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', zIndex: 1000,
                      display: 'flex', alignItems: 'center', justifyContent: 'center' }}
             onClick={() => setShowConvert(false)}>
          <div style={{ background: 'var(--bg-surface)', borderRadius: 16, padding: 28, width: 420, boxShadow: '0 20px 60px rgba(0,0,0,.2)' }}
               onClick={e => e.stopPropagation()}>
            <h3 style={{ fontSize: 17, fontWeight: 700, marginBottom: 6 }}>Convert to Active Customer</h3>
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 20 }}>
              This will mark <strong>{proj.customer_name}</strong> as an active customer. Optionally fill in AMC and serial details.
            </p>
            <form onSubmit={convertProspect}>
              {[['AMC Start', 'amc_start_date', 'date'], ['AMC End', 'amc_end_date', 'date']].map(([lbl, key, type]) => (
                <div key={key} style={{ marginBottom: 12 }}>
                  <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>{lbl}</label>
                  <input type={type} className="input" value={convertForm[key]} onChange={e => setConvertForm(f => ({ ...f, [key]: e.target.value }))} />
                </div>
              ))}
              <div style={{ marginBottom: 20 }}>
                <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Serial Number</label>
                <input className="input" value={convertForm.serial_number} onChange={e => setConvertForm(f => ({ ...f, serial_number: e.target.value }))} />
              </div>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button type="button" className="btn btn-ghost" onClick={() => setShowConvert(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary">Convert</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showApplyTpl && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', zIndex: 1000,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}
             onClick={() => setShowApplyTpl(false)}>
          <div style={{ background: 'var(--bg-surface)', borderRadius: 16, padding: 28, width: '100%', maxWidth: 460, boxShadow: '0 20px 60px rgba(0,0,0,.2)' }}
               onClick={e => e.stopPropagation()}>
            <h3 style={{ fontSize: 17, fontWeight: 700, marginBottom: 6 }}>Apply a template</h3>
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16, lineHeight: 1.6 }}>
              Its milestones and tasks are <strong>added</strong> to this project. Milestones with the same name are merged;
              tasks that already exist are skipped. Nothing existing is deleted — remove any leftover duplicates with the task <strong>Delete</strong> button.
            </p>
            <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', display: 'block', marginBottom: 6 }}>Template</label>
            <select className="input" value={applyTplId} onChange={e => setApplyTplId(e.target.value)}>
              <option value="">— Select a template —</option>
              {templates.map(t => (
                <option key={t.id} value={t.id}>
                  {t.name}{t.product_name ? ` · ${t.product_name}` : ''} ({t.milestone_count}m / {t.task_count}t){t.owner_id ? ' · Mine' : ''}
                </option>
              ))}
            </select>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 22 }}>
              <button type="button" className="btn btn-ghost" onClick={() => setShowApplyTpl(false)}>Cancel</button>
              <button type="button" className="btn btn-primary" disabled={!applyTplId || applyingTpl} style={{ background: '#0B6623' }} onClick={applyTemplate}>
                {applyingTpl ? 'Applying…' : 'Apply template'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
